/**
 * Public one-click unsubscribe page. Reachable WITHOUT a session — the token
 * in the URL is the auth. Idempotent: refreshing the page after success
 * doesn't re-trigger or error.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | done | resub | error
  const [email, setEmail] = useState("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setErrMsg("No unsubscribe token in the URL."); return; }
    api.post(`/auth/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => { setEmail(r.data.email); setState("done"); })
      .catch((e) => { setErrMsg(e.response?.data?.detail || "This link is invalid or expired."); setState("error"); });
  }, [token]);

  const resub = async () => {
    try {
      await api.post(`/auth/resubscribe?token=${encodeURIComponent(token)}`);
      setState("resub");
    } catch (e) {
      setErrMsg(e.response?.data?.detail || "Could not resubscribe.");
      setState("error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <div className="max-w-md w-full bg-white border-2 border-black p-8" data-testid="unsubscribe-page">
        <Logo size="sm" />
        {state === "idle" && (
          <div className="mt-6 text-sm text-neutral-500">Processing…</div>
        )}
        {state === "done" && (
          <>
            <div className="mt-6 overline text-mango">EMAIL PREFERENCES</div>
            <h1 className="text-2xl font-black tracking-tight mt-1">You're unsubscribed</h1>
            <p className="text-sm text-neutral-700 mt-3">
              <span className="font-mono bg-neutral-100 px-1.5">{email}</span> will no longer receive promotional emails from oodel.
            </p>
            <p className="text-sm text-neutral-700 mt-3">
              You can still <Link to="/login" className="underline font-semibold">sign in to oodel.co</Link> and take surveys — your dashboard access is unaffected.
            </p>
            <div className="mt-6 pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 mb-2">Changed your mind?</p>
              <Button onClick={resub} variant="outline" className="rounded-none border-2 border-black hover:bg-mango hover:text-white" data-testid="resub-btn">
                Re-subscribe to oodel emails
              </Button>
            </div>
          </>
        )}
        {state === "resub" && (
          <>
            <div className="mt-6 overline text-mango">DONE</div>
            <h1 className="text-2xl font-black tracking-tight mt-1">Welcome back</h1>
            <p className="text-sm text-neutral-700 mt-3">
              <span className="font-mono bg-neutral-100 px-1.5">{email}</span> is subscribed again. We'll only email you when surveys match your interests.
            </p>
            <Link to="/login" className="block mt-6 text-sm underline">Sign in to oodel →</Link>
          </>
        )}
        {state === "error" && (
          <>
            <div className="mt-6 overline text-red-600">SORRY</div>
            <h1 className="text-2xl font-black tracking-tight mt-1">Couldn't process</h1>
            <p className="text-sm text-neutral-700 mt-3">{errMsg}</p>
            <p className="text-xs text-neutral-500 mt-3">
              Email <a href="mailto:support@oodel.co" className="underline">support@oodel.co</a> and we'll unsubscribe you manually.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
